import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchLiveQuotes } from '../../services/apiClient';
import { useMarketPolling } from '../../hooks/useMarketPolling';
import { earningsService } from '../../services/earningsService';
import { EarningsDiscoveryRow, Grade } from '../../types/earnings';
import { fmt, gradeColor, tierLabel } from '../../utils/formatters';

const GRADE_LABELS: Record<Grade, string> = {
  'A+': 'A+ — Highest conviction',
  A: 'A — Full size',
  B: 'B — Full + directional',
  S: 'Skip',
};

interface DiscoverEarningsProps {
  prefetchedRows?: EarningsDiscoveryRow[] | null;
}

const DiscoverEarnings: React.FC<DiscoverEarningsProps> = ({ prefetchedRows }) => {
  const [rows, setRows] = useState<EarningsDiscoveryRow[]>(prefetchedRows ?? []);
  const [loading, setLoading] = useState(!prefetchedRows);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const activeRef = useRef(true);
  useEffect(() => { activeRef.current = true; return () => { activeRef.current = false; }; }, []);

  useEffect(() => {
    const applyData = (data: EarningsDiscoveryRow[]) => {
      if (!activeRef.current) return;
      setRows(data);
      const tickers = Array.from(new Set(data.map((r) => r.ticker)));
      fetchLiveQuotes(tickers).then((prices) => {
        if (!activeRef.current || Object.keys(prices).length === 0) return;
        setRows((prev) =>
          prev.map((r) => prices[r.ticker] != null
            ? { ...r, current_price: String(prices[r.ticker]) }
            : r
          ),
        );
      });
    };

    if (prefetchedRows) {
      applyData(prefetchedRows);
    } else {
      earningsService
        .getDiscoveryList()
        .then(applyData)
        .catch((e) => { if (activeRef.current) setError(e.message); })
        .finally(() => { if (activeRef.current) setLoading(false); });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Live price polling during market hours
  const pollTickers = useMemo(() => rows.map((r) => r.ticker), [rows]);
  useMarketPolling(pollTickers, useCallback((prices: Record<string, number>) => {
    setRows((prev) =>
      prev.map((r) => prices[r.ticker] != null ? { ...r, current_price: String(prices[r.ticker]) } : r),
    );
  }, []));

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!rows.length) return <Alert severity="info">No earnings events in the next 14 days for watched tickers.</Alert>;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Upcoming earnings in next 14 days. Click a row for full analysis and strategy recommendations.
      </Typography>
      <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700 } }}>
              <TableCell>Ticker</TableCell>
              <TableCell>Earnings Date</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Price</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                <Tooltip title="IV Edge = ATM straddle ÷ stock price ÷ historical avg move. ≥1.8x = A grade.">
                  <span>IV Edge</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">Grade</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Liquidity</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Recommended Strategy</TableCell>
              <TableCell align="right">Max Pain</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={`${r.ticker}-${r.earnings_date}`}
                hover
                sx={{
                  cursor: 'pointer',
                  opacity: r.grade === 'S' ? 0.5 : 1,
                }}
                onClick={() => navigate(`/discover/earnings/${r.ticker}?date=${r.earnings_date}`)}
              >
                <TableCell>
                  <Typography fontWeight={700}>{r.ticker}</Typography>
                </TableCell>
                <TableCell>{fmt.date(r.earnings_date)}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt.currency(r.current_price)}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {r.iv_edge_ratio
                    ? <Typography variant="body2" fontWeight={600}>{Number(r.iv_edge_ratio).toFixed(2)}x</Typography>
                    : <Typography variant="body2" color="text.secondary">—</Typography>
                  }
                </TableCell>
                <TableCell align="center">
                  {r.grade ? (
                    <Tooltip title={GRADE_LABELS[r.grade as Grade] ?? ''}>
                      <Chip
                        label={r.grade === 'S' ? 'Skip' : r.grade}
                        size="small"
                        sx={{
                          bgcolor: gradeColor(r.grade),
                          color: 'white',
                          fontWeight: 700,
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2">{tierLabel(r.liquidity_tier)}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2">
                    {r.recommended_strategy
                      ? r.recommended_strategy.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                      : <span style={{ color: '#9e9e9e' }}>—</span>
                    }
                  </Typography>
                </TableCell>
                <TableCell align="right">{fmt.currency(r.max_pain)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DiscoverEarnings;
